import React, { useEffect, useRef } from 'react';

const GOL_INITIAL_LIFE_THRESHOLD = 0.85;
const GOL_REFRESH_TIME_MS = 250;
const GOL_CELL_RESOLUTION_PX = 10;

const LIVE_CELL_COLOR = 'rgba(255,255,255,0.075)';
const DEAD_CELL_COLOR = '#212121';

const createGrid = ({ cols, rows }) =>
  new Array(cols).fill(null).map(() => new Array(rows).fill(0));

// create boolean map of initial living and dead cells
const randomizeGrid = ({ grid }) =>
  grid.map((col) => col.map(() => +(Math.random() > GOL_INITIAL_LIFE_THRESHOLD)));

const drawGrid = ({ grid, rows, cols, ctx, canvas }) => {
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let col = 0; col < cols; col += 1) {
    for (let row = 0; row < rows; row += 1) {
      const cell = grid[col][row];
      ctx.beginPath();

      ctx.rect(
        col * GOL_CELL_RESOLUTION_PX,
        row * GOL_CELL_RESOLUTION_PX,
        GOL_CELL_RESOLUTION_PX,
        GOL_CELL_RESOLUTION_PX,
      );
      ctx.fillStyle = cell ? LIVE_CELL_COLOR : DEAD_CELL_COLOR;

      ctx.fill();
    }
  }

  ctx.stroke();
};

const nextGeneration = ({ grid, cols, rows }) => {
  const nextGrid = grid.map((arr) => [...arr]);

  for (let col = 0; col < grid.length; col += 1) {
    for (let row = 0; row < grid[col].length; row += 1) {
      const cell = grid[col][row];
      let numNeighbors = 0;

      for (let i = -1; i < 2; i += 1) {
        for (let j = -1; j < 2; j += 1) {
          if (i === 0 && j === 0) {
            continue;
          }

          const x = col + i;
          const y = row + j;

          if (x >= 0 && x < cols && y >= 0 && y < rows) {
            numNeighbors += grid[x][y];
          }
        }
      }

      if (cell === 1 && (numNeighbors < 2 || numNeighbors > 3)) {
        nextGrid[col][row] = 0;
      } else if (cell === 0 && numNeighbors === 3) {
        nextGrid[col][row] = 1;
      }
    }
  }

  return nextGrid;
};

const GameOfLifeCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d');

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const cols = Math.floor(canvas.width / GOL_CELL_RESOLUTION_PX);
    const rows = Math.floor(canvas.height / GOL_CELL_RESOLUTION_PX);

    let grid = createGrid({ cols, rows });
    grid = randomizeGrid({ grid });

    const update = () => {
      grid = nextGeneration({ grid, cols, rows });
      drawGrid({ ctx, grid, cols, rows, canvas });
    };

    drawGrid({ ctx, grid, cols, rows, canvas });
    const intervalId = setInterval(update, GOL_REFRESH_TIME_MS);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="height-max width-max"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: -1,
      }}
    />
  );
};

export default GameOfLifeCanvas;
